import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Users, Target, Heart, TrendingUp } from "lucide-react";

const About = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About HiStage",
    "description": "Learn about HiStage's mission to revolutionize live streaming by empowering creators and connecting fans through interactive engagement.",
    "url": "https://histage.lovable.app/about"
  };

  return (
    <>
      <SEO 
        title="About HiStage - Our Mission & Story"
        description="HiStage is revolutionizing live streaming by putting fans in control. Learn about our mission to empower creators and build meaningful connections through interactive live performances."
        url="/about"
        keywords={[
          "about histage",
          "live streaming mission",
          "creator platform story",
          "fan engagement platform",
          "interactive streaming company"
        ]}
        schema={schema}
      />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                Empowering Creators, Connecting Fans
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                HiStage is the fan-driven live streaming platform that puts audiences in control 
                and helps creators build meaningful connections through interactive performances.
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
                <div>
                  <h2 className="text-4xl font-bold text-foreground mb-6">Our Mission</h2>
                  <p className="text-lg text-muted-foreground mb-4">
                    We're building a platform where creators have complete ownership of their content 
                    and earnings, while fans get unprecedented access to shape live performances.
                  </p>
                  <p className="text-lg text-muted-foreground">
                    Through interactive voting, tipping, and real-time engagement, we're transforming 
                    passive viewers into active participants in the creative process.
                  </p>
                </div>
                <div className="clean-card rounded-2xl p-8 bg-gradient-to-br from-primary/10 to-accent/10">
                  <div className="space-y-6">
                    {[
                      { icon: Users, title: "Fan-Powered", desc: "Audiences shape the show" },
                      { icon: Target, title: "Creator-Owned", desc: "Full control & fair earnings" },
                      { icon: Heart, title: "Community First", desc: "Building real connections" },
                      { icon: TrendingUp, title: "Innovation", desc: "Pushing boundaries" }
                    ].map((value, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <value.icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-1">{value.title}</h3>
                          <p className="text-sm text-muted-foreground">{value.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Story */}
              <div className="clean-card rounded-2xl p-12">
                <h2 className="text-3xl font-bold text-foreground mb-6">Our Story</h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground mb-4">
                    HiStage was born from a simple observation: live streaming had become one-sided. 
                    Creators performed, and audiences watched. We believed there was a better way.
                  </p>
                  <p className="text-muted-foreground mb-4">
                    We imagined a platform where fans could vote on setlists, unlock exclusive content 
                    through collective tipping, and genuinely influence the direction of live shows. 
                    A place where creators earned fairly and maintained full ownership of their work.
                  </p>
                  <p className="text-muted-foreground">
                    Today, HiStage powers thousands of live performances every month, from intimate 
                    acoustic sessions to large-scale concerts, comedy shows, gaming streams, and more. 
                    We're just getting started.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                Join Our Community
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Be part of the movement reshaping live entertainment
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth">
                  <Button size="lg" className="rounded-full px-8">
                    Get Started
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="rounded-full px-8">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About;
